// Read-side counterpart to `apiMutate` for the simple GET-and-throw
// shape: cookie-auth fetch, parse JSON, throw on non-2xx with the
// caller's failureLabel + status code, optionally fold a known
// "absent" status into a `null` return so callers can treat
// "not found" as a value rather than an exception.
//
// Not used by every GET in the SPA — many sites have caller-specific
// behaviours that don't map onto throw-or-return-null:
//
//   - `wiki_pages.fetchContent` flips `needsAuth` on 401 (citation
//     hover-cards re-trigger the auth gate)
//   - `source_activity.fetchSlice` writes `loadError` ref directly
//     instead of throwing
//   - `useSidebarSearch` mutates `results`/`error` refs in-place
//   - `threads.loadRecent` swallows failures (best-effort load)
//   - `threads.open` distinguishes 404 from other failures via
//     `submitError`
//
// Those stay inline. Forcing them through `apiGet` would either
// require kwargs for every special case (widening the seam) or
// double the call site (apiGet + a wrapper).
//
// Usage:
//
//   const note = await apiGet<LintNote>(`/api/v1/lint_notes/${id}`, {
//     failureLabel: `Failed to load lint note ${id}`,
//     tolerateStatuses: [ 404 ],
//   })
//   if (!note) return  // 404 → null
//
// Pass `onError` to suppress re-throwing (best-effort callers):
//
//   const data = await apiGet<T>(url, {
//     failureLabel: '…',
//     onError: () => {},      // silent failure → returns null
//   })

export interface ApiGetOptions {
  // Human-readable label baked into the fallback error message.
  // Phrased as a verbed failure clause: e.g. `"Failed to load wiki page 7"`.
  failureLabel: string
  // HTTP status codes that should resolve to `null` rather than
  // throwing. Typical use: `[404]` for "this row may not exist."
  tolerateStatuses?: readonly number[]
  // When provided, called with the Error instead of re-throwing.
  // The function returns `null` so callers can safely ignore the result.
  onError?: (e: Error) => void
}

// Overloads — narrowest first so TypeScript picks the tightest match:
//   onError present → T | null (errors call onError and return null)
export function apiGet<T>(url: string, options: ApiGetOptions & { onError: (e: Error) => void }): Promise<T | null>
//   tolerateStatuses present → T | null
export function apiGet<T>(
  url: string,
  options: ApiGetOptions & { tolerateStatuses: readonly number[] },
): Promise<T | null>
//   neither → T (throws on any failure)
export function apiGet<T>(url: string, options: ApiGetOptions): Promise<T>
export async function apiGet<T>(url: string, options: ApiGetOptions): Promise<T | null> {
  try {
    const res = await fetch(url, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })

    if (options.tolerateStatuses?.includes(res.status)) return null
    if (!res.ok) throw new Error(`${options.failureLabel}: ${res.status}`)
    return (await res.json()) as T
  } catch (e) {
    if (options.onError) {
      options.onError(e instanceof Error ? e : new Error(String(e)))
      return null
    }
    throw e
  }
}
